import React, { useCallback, useMemo, useContext, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TableSortLabel,
  Backdrop,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import { getNextOrderNumber } from '../components/orderCounter';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoModalV2 from '../components/InfoModalV2';
import { createOrderSchema } from '../modals/OrderSchema';
import { useGenerateTasksForOrder } from '../utils/orderTaskGenerator';
import { doc, deleteDoc, addDoc, collection, query, where, getDocs, increment, updateDoc } from 'firebase/firestore';
import { parseISO, format, differenceInCalendarDays } from 'date-fns';
import { db } from '../firebaseConfig';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { AppContext } from '../AppContext';
import AddOrder from '../components/AddOrder';
import InvoiceTable from '../components/InvoiceTable';
import { SnackbarContext } from '../SnackbarContext';
import useIsMobile from '../utils/useIsMobile';

import { trackFeature } from '../utils/analytics';

// Styled table cell with sort label overrides
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  '& .MuiTableSortLabel-root': {
    color: `${theme.palette.common.white} !important`,
    '&.Mui-active': {
      color: `${theme.palette.common.white} !important`,
    },
    '& .MuiTableSortLabel-icon': {
      color: `${theme.palette.common.white} !important`,
    },
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.75) !important',
    },
  },
}));

// Cloud function call to generate an invoice.
// This function posts to your deployed cloud function endpoint.
async function generateInvoiceFromOrder(orderId, userId, farmId) {
  try {
    const response = await fetch(
      'https://us-central1-micgro-manager.cloudfunctions.net/generateInvoice',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId, userId, farmId }),
      }
    );
    if (!response.ok) {
      let errorMsg = '';
      try {
        const errorData = await response.json();
        errorMsg = errorData.message || JSON.stringify(errorData);
      } catch {
        errorMsg = await response.text();
      }
      throw new Error(`HTTP Error ${response.status}: ${errorMsg}`);
    }
    const data = await response.json();
    if (data.success) {
      return data.pdfUrl;
    } else {
      throw new Error(data.message || 'Unknown error during invoice generation.');
    }
  } catch (error) {
    console.error('Error generating invoice:', error);
    throw error;
  }
}

function Orders() {
  const { user, orders, harvests, seeds, userData, invoices, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';
  const isMobile = useIsMobile();

  // Build a lookup for harvests.
  const harvestLookup = useMemo(() => {
    return harvests.reduce((lookup, harvest) => {
      lookup[String(harvest.id)] = harvest;
      return lookup;
    }, {});
  }, [harvests]);

  // Build a lookup for seeds.
  const seedLookup = useMemo(() => {
    return seeds.reduce((lookup, seed) => {
      lookup[String(seed.id)] = seed;
      return lookup;
    }, {});
  }, [seeds]);

  // Sorting state for orders.
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortColumn, setSortColumn] = useState('orderNumber');
  const [cloneOrderData, setCloneOrderData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeOrderObj, setCompleteOrderObj] = useState(null);
  const [orderBeingCompleted, setOrderBeingCompleted] = useState(null);
  const subscriptionActive = isSubscriptionActive(userData);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedOrderData, setSelectedOrderData] = useState(null);

  const { showSuccess, showError } = useContext(SnackbarContext);

  const generateTaskForOrderDelivery = useGenerateTasksForOrder();

  // Sorting orders.
  const sortedOrders = useMemo(() => {
    const data = [...orders];
    data.sort((a, b) => {
      let aValue, bValue;
      if (sortColumn === 'orderNumber') {
        const aNum = a.orderNumber || 0;
        const bNum = b.orderNumber || 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      } else if (sortColumn === 'customerName') {
        aValue = a.customerName || '';
        bValue = b.customerName || '';
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (sortColumn === 'deliveryDate') {
        const dateA = a.deliveryDate ? parseISO(a.deliveryDate) : new Date(0);
        const dateB = b.deliveryDate ? parseISO(b.deliveryDate) : new Date(0);
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (sortColumn === 'timeUntil') {
        // Calculate days until delivery date for each order.
        const dateA = a.deliveryDate ? parseISO(a.deliveryDate) : new Date(0);
        const dateB = b.deliveryDate ? parseISO(b.deliveryDate) : new Date(0);
        const daysDiffA = differenceInCalendarDays(dateA, new Date());
        const daysDiffB = differenceInCalendarDays(dateB, new Date());
        return sortDirection === 'asc' ? daysDiffA - daysDiffB : daysDiffB - daysDiffA;
      } else if (sortColumn === 'recurring') {
        // Convert recurring value to a number for sorting.
        // For recurring orders, use frequency * multiplier (days:1, week:7, month:30).
        const getRecurringSortValue = (order) => {
          if (order.recurring && typeof order.recurring === 'object') {
            const frequency = Number(order.recurring.frequency) || 0;
            const period = order.recurring.period;
            let multiplier = 1;
            if (period === 'week') multiplier = 7;
            else if (period === 'month') multiplier = 30;
            return frequency * multiplier;
          }
          return 0;
        };
        const aRecurring = getRecurringSortValue(a);
        const bRecurring = getRecurringSortValue(b);
        return sortDirection === 'asc' ? aRecurring - bRecurring : bRecurring - aRecurring;
      }
      return 0;
    });
    return data;
  }, [orders, sortColumn, sortDirection]);


  // Pagination.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const paginatedOrders = useMemo(
    () => sortedOrders.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [sortedOrders, page, rowsPerPage]
  );

  async function adjustPortionsLeft(produceArray, multiplier = -1) {
    if (!activeFarmId) {
      throw new Error('No active farm selected.');
    }
    for (const entry of produceArray) {
      const totalPortions = Number(entry.portions) || 0;
      for (const line of entry.items) {
        const harvestId = line.harvestId;
        const prop = Number(line.proportion) || 0;
        const delta = multiplier * totalPortions * prop;
        if (!harvestId || delta === 0) continue;

        const harvestRef = doc(db, 'farms', activeFarmId, 'harvests', harvestId);
        await updateDoc(harvestRef, {
          portionsLeft: increment(delta),
        });
      }
    }
  }

  // "Complete" order handler: calls the cloud function.
  const handleConfirmComplete = async () => {
    if (!user || !completeOrderObj) return;
    if (!activeFarmId) {
      showError?.('No active farm selected.');
      return;
    }
    setOrderBeingCompleted(completeOrderObj.id);
    setCompleteOpen(false);
    setCompleteOrderObj(null);
    try {
      const pdfUrl = await generateInvoiceFromOrder(completeOrderObj.id, user.uid, activeFarmId);
      if (pdfUrl) {
        trackFeature('order_completed', {
          orderId:     completeOrderObj.id,
          orderNumber: completeOrderObj.orderNumber,
          invoiceUrl:  pdfUrl,
        });
        showSuccess?.('Invoice created, original order deleted.');
        // Here you can also update the order status or remove the order from active orders.
      }
    } catch (error) {
      showError?.('Error completing order.');
    } finally {
      setOrderBeingCompleted(null);
    }
  };

  const handleCompleteClick = (order) => {
    setCompleteOrderObj(order);
    setCompleteOpen(true);
  };

  const handleCompleteCancel = () => {
    setCompleteOpen(false);
    setCompleteOrderObj(null);
  };

  // Dialog and confirmation handlers.
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setOrderToEdit(null);
  };
  const handleAddOrder = () => {
    handleCloseDialog();
  };

  const openConfirmDialog = (order) => {
    setConfirmOrder(order);
    setConfirmOpen(true);
  };
  async function handleConfirmDelete() {
    if (!confirmOrder) return;
    if (!activeFarmId) {
      showError('No active farm selected.');
      return;
    }
    try {
      // 1) Build a “clean” produce array that only includes still-existent harvests:
      const existingIds = new Set(harvests.map(h => String(h.id)));
      const validProduce = confirmOrder.produce
        .map(entry => ({
          ...entry,
          items: entry.items.filter(it => existingIds.has(it.harvestId))
        }))
        // drop any product tabs that ended up with zero lines
        .filter(entry => entry.items.length > 0);

      // 2) Give back stock only for the valid lines
      await adjustPortionsLeft(validProduce, +1);

      // 3) Delete associated tasks and the order itself as before...
      const tasksQuery = query(
        collection(db, 'farms', activeFarmId, 'tasks'),
        where('orderId', '==', confirmOrder.id)
      );
      const tasksSnap = await getDocs(tasksQuery);
      await Promise.all(tasksSnap.docs.map(d => deleteDoc(d.ref)));
      await deleteDoc(doc(db, 'farms', activeFarmId, 'orders', confirmOrder.id));

      showSuccess('Order deleted and any remaining portions restored.');
      trackFeature('order_deleted', {
        orderId:     confirmOrder.id,
        orderNumber: confirmOrder.orderNumber,
      });
    } catch (error) {
      console.error(error);
      showError('Failed to delete order.');
    } finally {
      setConfirmOpen(false);
      setConfirmOrder(null);
    }
  }



  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setConfirmOrder(null);
  };

  // Menu handlers.
  const handleMenuOpen = (event, order) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedOrder(null);
  };
  const handleEdit = () => {
    trackFeature('order_edit_clicked', {
      orderId: selectedOrder.id,
      orderNumber: selectedOrder.orderNumber,
    });
    setEditMode(true);
    setOrderToEdit(selectedOrder);
    handleOpenDialog();
    handleMenuClose();
  };

  const handleClone = async () => {
    if (!selectedOrder || !user) return;
    if (!activeFarmId) {
      showError('No active farm selected.');
      return;
    }
    // 1) normalize
    const normalizeProduce = (prodField) => {
      if (Array.isArray(prodField)) return prodField;
      const ids = prodField.split('/');
      const share = 1 / ids.length;
      return ids.map(hid => ({
        items: [{ harvestId: hid, proportion: share }],
        portions: 1,
      }));
    };
    let newProd = normalizeProduce(selectedOrder.produce);
    // 1a) drop lines for harvests that no longer exist
    const existing = new Set(harvests.map(h => String(h.id)));
    newProd = newProd
      .map(entry => ({
        ...entry,
        items: entry.items.filter(it => existing.has(it.harvestId)),
      }))
      .filter(entry => entry.items.length > 0);

    // 2) STOCK CHECK
    const insufficient = newProd.some(entry =>
      entry.items.some(line => {
        const h = harvests.find(h => String(h.id) === line.harvestId);
        const avail = h ? Number(h.portionsLeft) : 0;
        const need = entry.portions * line.proportion;
        return avail < need;
      })
    );
    if (insufficient) {
      showError(
        'Not enough portions to auto-clone that order. ‘Edit’ mode opened so you can adjust the harvest selection before saving.'
      );
      // open the add/edit dialog prefilled
      setEditMode(false);                     // ensure it’s treated as a new order
      setOrderToEdit({                        // prefill form with everything from the original
        ...selectedOrder,
        produce: newProd,
      });
      handleOpenDialog();
      return;
    }

    // 3) If stock is fine, proceed with an actual clone:
    try {
      const { id, orderNumber, ...data } = selectedOrder;
      const newNumber = await getNextOrderNumber(activeFarmId);
      const payload = {
        ...data,
        orderNumber: newNumber,
        produce: newProd,
      };
      const ref = await addDoc(
        collection(db, 'farms', activeFarmId, 'orders'),
        payload
      );
      // 4) subtract the cloned portions
      await adjustPortionsLeft(newProd, -1);
      // 5) create delivery task
      const task = generateTaskForOrderDelivery({
        orderId: ref.id,
        orderNumber: newNumber,
        deliveryDate: selectedOrder.deliveryDate,
      });
      if (task) await addDoc(collection(db, 'farms', activeFarmId, 'tasks'), task);
      trackFeature('order_cloned', {
        fromOrderId: selectedOrder.id,
        fromOrderNumber: selectedOrder.orderNumber,
        toOrderId: ref.id,
        toOrderNumber: newNumber,
      });
      showSuccess('Order cloned successfully.');
    } catch (err) {
      console.error(err);
      showError('Error cloning order.');
    } finally {
      handleMenuClose();
    }
  };




  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
  };



  // Helper: Format delivery date and compute "time until".
  function formatOrderDate(isoDate) {
    let displayDate = isoDate;
    let timeUntil = '';
    let daysDiff = 999999;
    if (isoDate) {
      try {
        const dateObj = parseISO(isoDate);
        displayDate = format(dateObj, defaultDateFormat);
        daysDiff = differenceInCalendarDays(dateObj, new Date());
        if (daysDiff < 0) {
          timeUntil = `${Math.abs(daysDiff)} days overdue`;
        } else if (daysDiff === 0) {
          timeUntil = 'Today';
        } else {
          timeUntil = `${daysDiff} days`;
        }
      } catch (error) {
        console.error('Error parsing ISO date:', isoDate, error);
      }
    }
    return { displayDate, timeUntil, daysDiff };
  }

  const shortFormat = defaultDateFormat.includes('yyyy')
    ? defaultDateFormat.replace(/[-/]?yyyy/, '')
    : defaultDateFormat;

  // Returns the produce currency from the first produce item, or '£' as fallback.
  const getProduceCurrency = (order) => {
    if (order.produce && order.produce.length > 0 && order.produce[0].priceCurrency) {
      return order.produce[0].priceCurrency;
    }
    return '£';
  };

  // Returns the delivery currency from the order, or '£' as fallback.
  const getDeliveryCurrency = (order) => {
    if (order.deliveryPriceCurrency) {
      return order.deliveryPriceCurrency;
    }
    return '£';
  };

  const computeProduceTotal = (order) => {
    let produceTotal = 0;
    if (order.produce && Array.isArray(order.produce)) {
      produceTotal = order.produce.reduce((acc, prod) => {
        const portions = Number(prod.portions) || 0;
        const price = Number(prod.price) || 0;
        return acc + portions * price;
      }, 0);
    }
    return produceTotal;
  };

  const getDeliveryPrice = (order) => {
    return Number(order.deliveryPrice) || 0;
  };

  // In Orders.js, update this helper:
  const formatProduceNames = (order) => {
    if (Array.isArray(order.produce)) {
      // For each “product” tab
      const productLabels = order.produce.map((prod) => {
        // Harvest IDs may live in prod.items (new blends) or prod.harvestId (legacy)
        const harvestIds = Array.isArray(prod.items)
          ? prod.items.map((it) => it.harvestId)
          : prod.harvestId
            ? [prod.harvestId]
            : [];

        // Map each harvestId → "SeedName(Date)"
        const labels = harvestIds.map((id) => {
          const h = harvestLookup[String(id)];
          if (!h) return 'Deleted';
          // Build date string
          const dateLabel = h.harvestDate
            ? format(parseISO(h.harvestDate), shortFormat)
            : 'No Date';
          // Build seed name
          let seedName = 'Unknown';
          if (Array.isArray(h.seeds) && h.seeds.length) {
            seedName = h.seeds
              .map((s) =>
                s.seedId && seedLookup[s.seedId]
                  ? seedLookup[s.seedId].name
                  : 'Unknown'
              )
              .join('/');
          } else if (h.seedId && seedLookup[h.seedId]) {
            seedName = seedLookup[h.seedId].name;
          }
          // **Swapped**: seed first, then date in parentheses
          return `${seedName}(${dateLabel})`;
        });

        // Join multiple lines in a blend with "/"
        return labels.join('/');
      });

      // Join multiple products with ", "
      return productLabels.join(', ');
    }

    // Legacy string‑based produce
    if (typeof order.harvests === 'string') {
      const ids = order.harvests.split('/');
      return ids
        .map((id) => {
          const h = harvestLookup[String(id)];
          if (!h) return 'Deleted';
          const dateLabel = h.harvestDate
            ? format(parseISO(h.harvestDate), shortFormat)
            : 'No Date';
          let seedName = 'Unknown';
          if (h.seeds?.length) {
            seedName = h.seeds
              .map((s) =>
                s.seedId && seedLookup[s.seedId]
                  ? seedLookup[s.seedId].name
                  : 'Unknown'
              )
              .join('/');
          } else if (h.seedId && seedLookup[h.seedId]) {
            seedName = seedLookup[h.seedId].name;
          }
          return `${seedName}(${dateLabel})`;
        })
        .join(', ');
    }

    return '';
  };


  const formatDate = useCallback((value) => {
    if (!value) return '';
    try {
      return format(parseISO(value), defaultDateFormat);
    } catch (error) {
      return value;
    }
  }, [defaultDateFormat]);

  const toDate = useCallback((value) => {
    if (!value) return null;
    try {
      const d = parseISO(value);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch (error) {
      return null;
    }
  }, []);

  const orderSchema = useMemo(() => {
    return createOrderSchema({
      formatDate,
      formatProduceNames,
      computeProduceTotal,
      getDeliveryPrice,
      getDeliveryCurrency,
      getProduceCurrency,
      toDate,
      variant: 'full',
    });
  }, [
    formatDate,
    formatProduceNames,
    computeProduceTotal,
    getDeliveryPrice,
    getDeliveryCurrency,
    getProduceCurrency,
    toDate,
  ]);


  const handleRowClick = (order) => {
    setSelectedOrderData(order);
    setInfoModalOpen(true);
  };

  return (
    <Box
      sx={{
        maxWidth: '80%',
        py: '3',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '15px',
        '@media (max-width: 600px)': { maxWidth: '100%' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Orders</Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            trackFeature('order_add_clicked');
            handleOpenDialog();
          }}
          sx={{ padding: '12px 24px' }}
          disabled={!subscriptionActive}
        >
          Add Order
        </Button>
      </Box>

      {/* Active Orders Table */}
      {isMobile && (
        <Paper sx={{ mb: 2 }}>
          <List disablePadding>
            {paginatedOrders.map((order) => {
              const { displayDate, timeUntil, daysDiff } = formatOrderDate(order.deliveryDate);
              const statusColor = daysDiff < 0 ? 'warning' : daysDiff === 0 ? 'secondary' : 'default';
              return (
                <React.Fragment key={order.id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleRowClick(order)} sx={{ alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip size="small" color={statusColor} label={displayDate || 'No date'} />
                            <Typography variant="subtitle2">
                              {order.orderNumber || order.orderID}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {formatProduceNames(order)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {`${getDeliveryCurrency(order)}${(
                                computeProduceTotal(order) + getDeliveryPrice(order)
                              ).toFixed(2)}`} - {timeUntil}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              );
            })}
            {sortedOrders.length === 0 && (
              <ListItem>
                <ListItemText primary="No orders found." />
              </ListItem>
            )}
          </List>
        </Paper>
      )}
      <TableContainer component={Paper} sx={{ mb: 4, display: { xs: 'none', sm: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <TableSortLabel
                  active={sortColumn === 'orderNumber'}
                  direction={sortColumn === 'orderNumber' ? sortDirection : 'asc'}
                  onClick={() => handleSort('orderNumber')}
                >
                  Order Number
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={sortColumn === 'customerName'}
                  direction={sortColumn === 'customerName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('customerName')}
                >
                  Customer
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Produce</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Mouse over for full list.
                      </Typography>
                    }>
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Subtotal</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Items only, before delivery
                      </Typography>
                    }>
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Total</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Items & delivery cost
                      </Typography>
                    }>
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={sortColumn === 'deliveryDate'}
                  direction={sortColumn === 'deliveryDate' ? sortDirection : 'asc'}
                  onClick={() => handleSort('deliveryDate')}
                >
                  Delivery Date
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={sortColumn === 'timeUntil'}
                  direction={sortColumn === 'timeUntil' ? sortDirection : 'asc'}
                  onClick={() => handleSort('timeUntil')}
                >
                  Time Until
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Notes</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Mouse over note to read entire entry.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Complete</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Completing an order will remove it from active orders and create an invoice.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrders.map((order) => {
              const { displayDate, timeUntil, daysDiff } = formatOrderDate(order.deliveryDate);
              return (
                <TableRow
                  key={order.id}
                  onClick={() => handleRowClick(order)}
                  sx={(theme) => ({
                    cursor: 'pointer',
                    backgroundColor:
                      daysDiff < 0
                        ? theme.palette.mode === 'dark'
                          ? theme.palette.warning.dark
                          : theme.palette.warning.lighter
                        : daysDiff === 0
                          ? theme.palette.mode === 'dark'
                            ? theme.palette.secondary.dark
                            : theme.palette.secondary.lighter
                          : 'inherit',
                  })}
                >
                  <TableCell>{order.orderNumber || order.orderID}</TableCell>
                  <TableCell>{order.customerName}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 100,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Tooltip
                      title={<Typography sx={{ fontSize: '0.8rem' }}>{formatProduceNames(order)}</Typography>}
                    >
                      <Box
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatProduceNames(order)}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {`${getProduceCurrency(order)}${computeProduceTotal(order).toFixed(2)}`}
                  </TableCell>
                  <TableCell>
                    {`${getDeliveryCurrency(order)}${(
                      computeProduceTotal(order) + getDeliveryPrice(order)
                    ).toFixed(2)}`}
                  </TableCell>


                  <TableCell>{displayDate}</TableCell>
                  <TableCell>{timeUntil}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 100,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Tooltip
                      title={<Typography sx={{ fontSize: '0.8rem' }}>{order.notes || ''}</Typography>}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {order.notes || ''}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="contained"
                      color="tertiary"
                      size="small"
                      endIcon={<StorefrontIcon />}
                      onClick={() => handleCompleteClick(order)}
                      disabled={orderBeingCompleted === order.id}
                      sx={{
                        bgcolor: 'tertiary.dark',
                        '&:hover': { bgcolor: 'tertiary.darker' },
                      }}
                    >
                      Complete
                    </Button>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <IconButton onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, order); }}>
                      <SettingsIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {paginatedOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No orders found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedOrders.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Past Orders / Invoices Table */}
      <InvoiceTable
        invoices={invoices || []}
        setSuccess={showSuccess}
        setError={showError}
      />


      {/* Actions Menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem
          disabled={!subscriptionActive}
          onClick={() => {
            handleMenuClose();
            handleEdit();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          disabled={!subscriptionActive}
          onClick={handleClone}
        >
          Clone
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openConfirmDialog(selectedOrder);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Add Order Dialog */}
      <AddOrder
        open={openDialog}
        onClose={handleCloseDialog}
        onAdd={handleAddOrder}
        editMode={editMode}
        orderToEdit={orderToEdit}
        setSuccess={showSuccess}
        setError={showError}
      />

      {/* Confirmation Dialog for Deletion */}
      <ConfirmationDialog
        open={confirmOpen}
        title="Confirm Order Deletion"
        message="Are you sure you want to delete this order?"
        onConfirm={handleConfirmDelete}
        onClose={handleConfirmCancel}
      />

      {/* Confirmation Dialog for Completing an Order */}
      <ConfirmationDialog
        open={completeOpen}
        title="Complete Order"
        message="Are you sure you want to complete this order? This will remove it from active orders and create an invoice."
        onConfirm={handleConfirmComplete}
        onClose={handleCompleteCancel}
      />

      {/* Backdrop for loading indicator */}
      <Backdrop
        open={Boolean(orderBeingCompleted)}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999 }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Creating Invoice...
        </Typography>
      </Backdrop>

      <InfoModalV2
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        data={selectedOrderData}
        schema={orderSchema}
      />
    </Box>
  );
}

export default Orders;

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) {
    return true;
  }
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}




